import { useEffect, useState } from 'react';
import useNavigationStore from '@src/stores/navigationStore';
import useFilesStore from '@src/stores/filesStore';

import styles from './BeneficiariesDocuments.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import FileUploadInput from '@src/components/_shared/UI/FileUploadInput';
import ConfirmModal from '@src/components/_shared/ConfirmModal';
import { EdealDocument, DocumentLabelType, DocumentType, DocumentTypeDetail } from '@src/utilities/types';
import toast from 'react-hot-toast';
import { MAXSIZEFILE } from '@src/utilities/constants';
import { useMutation } from '@tanstack/react-query';
import { companyFileUpload } from '@src/api/Registration';
import useRegistrationStore from '@src/stores/registrationStore';
import InfoBlueBox from '@src/components/_shared/InfoBlueBox';

const filesTypes: Record<number, string> = {
    1: 'Pièce d’identité',
    2: 'Passeport',
    3: 'Carte de résident',
    4: 'Permis de conduire'
};

const documentTypeMapping: Record<string, string> = {
    'Pièce d’identité': 'idcard',
    'Passeport': 'passport',
    'Carte de résident': 'residence',
    'Permis de conduire': 'driving'
};

export default function BeneficiariesDocuments() {
    const { setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();
    const { files, addBeneficiaryFile } = useFilesStore();

    const [currentIndex, setCurrentIndex] = useState<number>(1);
    const [documentType, setDocumentType] = useState<string>('');
    
    const [keyState, setKeyState] = useState<Record<number, { frontKey: number; backKey: number, addressKey: number }>>({});
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [fileToDelete, setFileToDelete] = useState<number | null>(null);
    const [identityDocumentsValidated, setIdentityDocumentsValidated] = useState<boolean>(false);

    const [uploadedDocuments, setUploadedDocuments] = useState<{ front: boolean; back: boolean; address: boolean; }>({ front: false, back: false, address: false });
    const [uploadErrors, setUploadErrors] = useState<{ front: string | null; back: string | null; address: string | null; }>({ front: null, back: null, address: null });

    const beneficiaries = registration.company.beneficial_owners;
    const beneficiary = beneficiaries[currentIndex];
    const beneficiaryInformations = beneficiary;

    const currentBeneficiaryFiles = files.person.beneficiaries.filter(Boolean).find((fileObj: any) => fileObj.index === currentIndex);
    const identityFrontFile = currentBeneficiaryFiles?.frontFile || null;
    const identityBackFile = currentBeneficiaryFiles?.backFile || null;
    const addressFile = currentBeneficiaryFiles?.address || null;

    const isPassport = documentType === 'Passeport';

    const findDoc = (docs: EdealDocument[], type: string, detail: string): EdealDocument | undefined => {
        return docs.find(d => d.type?.name === type && d.type_detail?.name === detail);
    }

    useEffect(() => {
        if (files.person.beneficiaries.length > 1) {
            const beneficiary = files.person.beneficiaries.filter(Boolean).find(
                (fileObj: any) => fileObj.index === currentIndex
            );
            if (beneficiary && beneficiary.frontFile) {
                const typeDetail = beneficiary.frontFile.type_detail;
                const mappedType = Object.keys(documentTypeMapping).find(
                    (key) => documentTypeMapping[key] === typeDetail
                );
                if (mappedType) {
                    setDocumentType(mappedType);
                } else {
                    setDocumentType('');
                }
            }
        }
        setUploadedDocuments({ front: false, back: false, address: false });
        setUploadErrors({ front: null, back: null, address: null });

        if(registration.company.beneficial_owners[currentIndex]?.documents?.length > 0) {
            const documents = registration.company.beneficial_owners[currentIndex].documents ?? [];
            if (!documents.length) return;

            const front = findDoc(documents, DocumentType.id, DocumentTypeDetail.idcard);
            const frontback = findDoc(documents, DocumentType.id, DocumentTypeDetail.passport);
            const back = findDoc(documents, DocumentType.id, DocumentTypeDetail.back);
            const address = findDoc(documents, DocumentType.address, DocumentTypeDetail.others);

            const hasIdentity = !!(front?.id && back?.id) || !!frontback?.id;
            const hasAddress = !!address?.id;

            setIdentityDocumentsValidated(hasIdentity);
            setUploadedDocuments({
                front: !!front || !!frontback,
                back: !!back,
                address: !!address,
            });

            if (hasIdentity && hasAddress) {
                if (currentIndex + 1 === registration.company.beneficial_owners.length) {
                    setBlock('CompanyDocuments');
                } else {
                    setCurrentIndex(currentIndex + 1);
                    setIdentityDocumentsValidated(false);
                }
            }
        }
    }, [files, currentIndex]);

    const { mutate: mutateBackDocument, isPending: isBackPending } = useMutation({
        mutationFn: (formData: FormData) => companyFileUpload(formData),
        onSuccess: () => {
            setUploadedDocuments(prev => ({ ...prev, back: true }));
            setUploadErrors(prev => ({ ...prev, back: null }));
            setIdentityDocumentsValidated(true);
        },
        onError: () => {
            setUploadErrors(prev => ({ ...prev, back: 'Une erreur est survenue lors de la validation du document.' }));
            setUploadedDocuments(prev => ({ ...prev, back: false }));
        },
    });

    const { mutate: mutateFrontDocument, isPending: isFrontPending } = useMutation({
        mutationFn: (formData: FormData) => companyFileUpload(formData),
        onSuccess: () => {
            setUploadedDocuments(prev => ({ ...prev, front: true }));
            setUploadErrors(prev => ({ ...prev, front: null }));
            if (isPassport) {
                setIdentityDocumentsValidated(true);
            } else if (identityBackFile) {
                const backFormData = createFormData(
                    identityBackFile.file,
                    DocumentType.id,
                    identityBackFile.type_detail
                );
                mutateBackDocument(backFormData);
            }
        },
        onError: () => {
            setUploadErrors(prev => ({ ...prev, front: 'Une erreur est survenue lors de la validation du document.' }));
            setUploadedDocuments(prev => ({ ...prev, front: false }));
        },
    });

    const { mutate: mutateAddressDocument, isPending: isAddressPending } = useMutation({
        mutationFn: (formData: FormData) => companyFileUpload(formData),
        onSuccess: () => {
            setUploadedDocuments(prev => ({ ...prev, address: true }));
            setUploadErrors(prev => ({ ...prev, address: null }));
            if (currentIndex + 1 === registration.company.beneficial_owners.length) {
                setBlock('CompanyDocuments');
            } else {
                setCurrentIndex(currentIndex + 1);
                setIdentityDocumentsValidated(false);
                setUploadedDocuments({ front: false, back: false, address: false });
                setUploadErrors({ front: null, back: null, address: null });
            }
        },
        onError: () => {
            setUploadErrors(prev => ({ ...prev, address: 'Une erreur est survenue lors de la validation du document.' }));
            setUploadedDocuments(prev => ({ ...prev, address: false }));
        }
    });

    const _handleSelectFileType = (value: string) => {
        const updatedBeneficiaries = files.person.beneficiaries.map((fileObj: any) =>
            fileObj && fileObj.index === currentIndex
                ? {
                    ...fileObj,
                    frontFile: null,
                    backFile: null,
                    address: null,
                    typeDetail: documentTypeMapping[filesTypes[Number(value)]] || '',
                }
                : fileObj
        );
        files.person.beneficiaries = updatedBeneficiaries;
        setDocumentType(filesTypes[Number(value)]);
        setKeyState((prev) => ({
            ...prev,
            [currentIndex]: {
                ...prev[currentIndex],
                frontKey: (prev[currentIndex]?.frontKey || 0) + 1,
                backKey: (prev[currentIndex]?.backKey || 0) + 1,
                addressKey: (prev[currentIndex]?.addressKey || 0) + 1,
            },
        }));
        setUploadErrors({ front: null, back: null, address: null });
    };

    const _handleSelectFile = (id: number, file: File, verso: boolean, address: boolean) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            return toast.error('Format de fichier non supporté. Veuillez choisir un fichier PDF ou une image (.png, .jpeg).', { position: 'top-center' });
        }
        if (file.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }
        const fileDetails = {
            id: id,
            fileName: file.name,
            fileSize: file.size,
            file: file,
            type: address ? DocumentType.address : DocumentType.id,
            type_detail: address ? DocumentTypeDetail.others : verso ? DocumentTypeDetail.back : documentTypeMapping[documentType] || '',
            label: file.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image,
        };

        if (address) {
            addBeneficiaryFile(currentIndex, undefined, undefined, fileDetails);
            setUploadErrors(prev => ({ ...prev, address: null }));
            setUploadedDocuments(prev => ({ ...prev, address: false }));
        } else if (verso) {
            addBeneficiaryFile(currentIndex, undefined, fileDetails, undefined);
            setUploadErrors(prev => ({ ...prev, back: null }));
            setUploadedDocuments(prev => ({ ...prev, back: false }));
        } else {
            addBeneficiaryFile(currentIndex, fileDetails, undefined, undefined);
            setUploadErrors(prev => ({ ...prev, front: null }));
            setUploadedDocuments(prev => ({ ...prev, front: false }));
        }

        setKeyState((prev) => ({
            ...prev,
            [currentIndex]: {
                ...prev[currentIndex],
                [address ? 'addressKey' : verso ? 'backKey' : 'frontKey']: (prev[currentIndex]?.[address ? 'addressKey' : verso ? 'backKey' : 'frontKey'] || 0) + 1,
            },
        }));
    };

    const _handleDeleteIdentityFile = (id: number) => {
        setFileToDelete(id);
        setIsModalOpen(true);
    };

    const _confirmDeleteFile = () => {
        if (fileToDelete !== null) {
            const updatedBeneficiaries = files.person.beneficiaries.map((fileObj: any) =>
                fileObj && fileObj.index === currentIndex
                    ? {
                        ...fileObj,
                        frontFile: fileToDelete === 1 ? null : fileObj.frontFile,
                        backFile: fileToDelete === 2 ? null : fileObj.backFile,
                        address: fileToDelete === 3 ? null : fileObj.address,
                    }
                    : fileObj
            );
            files.person.beneficiaries = updatedBeneficiaries;
            setKeyState((prev) => ({
                ...prev,
                [currentIndex]: {
                    ...prev[currentIndex],
                    [fileToDelete === 3 ? 'addressKey' : fileToDelete === 1 ? 'frontKey' : 'backKey']: (prev[currentIndex]?.[fileToDelete === 3 ? 'addressKey' : fileToDelete === 1 ? 'frontKey' : 'backKey'] || 0) + 1,
                },
            }));
            if (fileToDelete === 1) {
                setUploadErrors(prev => ({ ...prev, front: null }));
                setUploadedDocuments(prev => ({ ...prev, front: false }));
            } else if (fileToDelete === 2) {
                setUploadErrors(prev => ({ ...prev, back: null }));
                setUploadedDocuments(prev => ({ ...prev, back: false }));
            } else if (fileToDelete === 3) {
                setUploadErrors(prev => ({ ...prev, address: null }));
                setUploadedDocuments(prev => ({ ...prev, address: false }));
            }
        }
        setIsModalOpen(false);
        setFileToDelete(null);
    };

    const _cancelDeleteFile = () => {
        setIsModalOpen(false);
        setFileToDelete(null);
    };

    const getFileTypeKey = (documentType: string): string | undefined => {
        if (!documentType) return undefined;
        return Object.keys(filesTypes).find(
            (key) => filesTypes[Number(key)].toLowerCase() === documentType.trim().toLowerCase()
        );
    };

    const createFormData = (file: File, type: string, typeDetail: string): FormData => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('demand_id', registration.demand_id);
        formData.append('type', type);
        formData.append('type_detail', typeDetail);
        formData.append('fromWeb', 'true');
        formData.append('beneficialOwnerId', beneficiaryInformations?.data.id || '');
        if (registration.partner_informations.is_partner) formData.append('is_psi', 'true');
        if (registration.person_id) formData.append('person_id', registration.person_id);
        return formData;
    };

    const _onValidateIdentity = () => {
        if (!identityFrontFile?.file || !(identityFrontFile.file instanceof File)) {
            return toast.error("Le fichier recto téléchargé est invalide. Veuillez réessayer.");
        }
        if (!isPassport && (!identityBackFile?.file || !(identityBackFile.file instanceof File))) {
            return toast.error("Le fichier verso téléchargé est invalide. Veuillez réessayer.");
        }

        const frontFormData = createFormData(
            identityFrontFile.file,
            'id',
            identityFrontFile.type_detail
        );
        mutateFrontDocument(frontFormData);
    };

    const _onValidateAddress = () => {
        if (!addressFile?.file || !(addressFile.file instanceof File)) {
            return toast.error("Le fichier téléchargé est invalide. Veuillez réessayer.");
        }

        const addressFormData = createFormData(
            addressFile.file,
            DocumentType.address,
            addressFile.type_detail
        );
        if (!uploadedDocuments.address) {
            mutateAddressDocument(addressFormData);
        } else {
            if (currentIndex + 1 === registration.company.beneficial_owners.length) {
                setBlock('CompanyDocuments');
            } else {
                setCurrentIndex(currentIndex + 1);
                setIdentityDocumentsValidated(false);
            }
        }
    };

    return (
        <Container>
            {currentIndex < Number(registration.company.beneficial_owners.length) ?
                identityDocumentsValidated ?
                    <>
                        <BlockTitle title={`Justificatif de domicile de ${beneficiaryInformations?.data.person.civility} ${beneficiaryInformations?.data.person.firstname.charAt(0).toUpperCase() + beneficiaryInformations?.data.person.firstname.slice(1)} ${beneficiaryInformations?.data.person.lastname.toUpperCase()}, bénéficiaire effectif`} />
                        <Subtitle content="Télécharger un justificatif de domicile pour compléter votre inscription et commencer à investir" />
                        <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>
                        <InfoBlueBox
                            boxTitle="Important"
                            description="Le justificatif doit être à votre nom et dater de moins de trois mois. Sont acceptées : facture d'électricité, eau, gaz, téléphone ou internet."
                        />
                        <FileUploadInput
                            key={`proofOfAddress-${keyState[currentIndex]?.addressKey}`}
                            title={'Télécharger un document'}
                            documentName={addressFile?.fileName}
                            isValidated={!!addressFile?.fileName}
                            selectFile={(file) => _handleSelectFile(3, file, false, true)}
                            deleteFile={() => _handleDeleteIdentityFile(3)}
                            initialFileSize={addressFile?.fileSize}
                            accept=".pdf,image/png,image/jpeg"
                            hasError={!!uploadErrors.address}
                            errorMessage={uploadErrors.address || ''}
                        />
                        {addressFile?.fileName ? (
                            <ValidateButton 
                                title="Continuer" 
                                onClick={_onValidateAddress} 
                                onLoading={isAddressPending} 
                                disabled={isAddressPending} 
                            />
                        ) : null}
                        <ConfirmModal
                            isOpen={isModalOpen}
                            onConfirm={_confirmDeleteFile}
                            onCancel={_cancelDeleteFile}
                        />
                    </>
                    :
                    <>
                        <BlockTitle title={`Pièce d'identité de ${beneficiaryInformations?.data.person.civility} ${beneficiaryInformations?.data.person.firstname.charAt(0).toUpperCase() + beneficiaryInformations?.data.person.firstname.slice(1)} ${beneficiaryInformations?.data.person.lastname.toUpperCase()}, bénéficiaire effectif`} />
                        <Subtitle content="Veuillez charger une pièce d'identité :" />
                        <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>

                        <div style={{ width: '100%' }}>
                            <InfoBlueBox
                                boxTitle="Important"
                                description={isPassport ? "Le numéro de passeport, les noms, prénoms et signature doivent être visibles" : "Merci de charger le recto et le verso séparément."}
                            />
                            <DropdownList
                                key={`dropdown-${keyState[currentIndex]?.frontKey}`}
                                title="Type de pièce"
                                width="100%"
                                options={filesTypes}
                                selected={getFileTypeKey(documentType) || 'Choisissez'}
                                onSelect={(value) => _handleSelectFileType(value)}
                            />
                        </div>
                        {documentType || identityFrontFile ? (
                            <FileUploadInput
                                key={`front-${keyState[currentIndex]?.frontKey}`}
                                title={isPassport ? "Télécharger un document" : "Télécharger un document (recto)"}
                                documentName={identityFrontFile?.fileName}
                                isValidated={!!identityFrontFile?.fileName}
                                selectFile={(file) => _handleSelectFile(1, file, false, false)}
                                deleteFile={() => _handleDeleteIdentityFile(1)}
                                initialFileSize={identityFrontFile?.fileSize}
                                accept=".pdf,image/png,image/jpeg"
                                hasError={!!uploadErrors.front}
                                errorMessage={uploadErrors.front || ''}
                            />
                        ) : null}

                        {!isPassport && (identityFrontFile || identityBackFile) ? (
                            <FileUploadInput
                                key={`back-${keyState[currentIndex]?.backKey}`}
                                title={'Télécharger un document (verso)'}
                                documentName={identityBackFile?.fileName}
                                isValidated={!!identityBackFile?.fileName}
                                selectFile={(file) => _handleSelectFile(2, file, true, false)}
                                deleteFile={() => _handleDeleteIdentityFile(2)}
                                initialFileSize={identityBackFile?.fileSize}
                                accept=".pdf,image/png,image/jpeg"
                                hasError={!!uploadErrors.back}
                                errorMessage={uploadErrors.back || ''}
                            />
                        ) : null}

                        {(isPassport && identityFrontFile) ||(identityFrontFile && identityBackFile) ? (
                            <ValidateButton 
                                title={isPassport ? 'Valider le document d\'identité' : 'Valider les documents d\'identité'}
                                onClick={_onValidateIdentity} 
                                disabled={isFrontPending || isBackPending} 
                                onLoading={isFrontPending || isBackPending} 
                            />
                        ) : null}

                        <ConfirmModal
                            isOpen={isModalOpen}
                            onConfirm={_confirmDeleteFile}
                            onCancel={_cancelDeleteFile}
                        />
                    </>
                :null}
        </Container>
    );
}
